import { ReactElement, ReactNode } from 'react';
import { Card, CardContent, Box, Divider, Stack, Typography } from '@mui/material';
import { CabDropdown, CabIcon, CabSwitch, CabTimezoneInput, CabButton, CabTooltip } from '@CabComponents';
import { User, WeekStartDay } from '../../../store';
import { IoAdd, IoClose, IoLockClosedOutline } from 'react-icons/io5';
import {
  SchedulingPreferencesRequest, SchedulingPreferences, SchedulingPreferencesList,
} from '../../../store/cabinetApi/generated/scheduling';

const MAX_SECONDARY_TZ = 4;
const MAX_TZ_MSG = "You can only have up to 5 timezones at a time.";

export interface CalendarDefaultsProps {
  user: User | null | undefined;
  schedulingPrefs: SchedulingPreferencesList | undefined;
  onSchedulingPrefChange: (pref: Partial<SchedulingPreferences>) => void;
  openUpgradeModal: () => void;
  onUpdateDefaultSecondaryTz: (idx: number, newTz: string | null) => void;
  onAddDefaultSecondaryTz: () => void;
  onRemoveDefaultSecondaryTz: (idx: number) => void;
}

const SettingsSection = ({ title, children }: { title: string; children: ReactNode }) => (
  <Stack sx={{
    gap: 0.25
  }}>
    <Typography
      sx={{
        fontSize: 16,
        fontWeight: 700
      }}>
      {title}
    </Typography>
    <Stack sx={{
      gap: 0
    }}>
      {children}
    </Stack>
  </Stack>
);

const SectionSpacer = () => <Box sx={{ pt: 1.75 }} />;

const SettingField = ({ label, children }: { label: string; children: ReactNode }) => (
  <Box
    sx={{
      width: "100%",
      display: "flex",
      flexDirection: { xs: 'column', sm: 'row' },
      justifyContent: "space-between",
      alignItems: { xs: 'flex-start', sm: 'center' },
      gap: 1,
      paddingY: 1
    }}>
    <Typography>
      {label}
    </Typography>
    <Box
      sx={{
        display: "flex",
        justifyContent: { xs: 'flex-start', sm: 'flex-end' },
        maxWidth: "100%"
      }}>
      {children}
    </Box>
  </Box>
);

export const CalendarDefaults = ({
  user, schedulingPrefs, onSchedulingPrefChange, openUpgradeModal, onUpdateDefaultSecondaryTz,
  onAddDefaultSecondaryTz, onRemoveDefaultSecondaryTz,
}: CalendarDefaultsProps): ReactElement | null => {
  const secondaryTimezones = schedulingPrefs?.user_prefs?.default_secondary_timezone;
  const reachedMaxTz = !!secondaryTimezones && secondaryTimezones.length >= MAX_SECONDARY_TZ;

  if (!user || !schedulingPrefs?.user_prefs) return null;

  return (
    <Card variant="outlined" sx={{ width: '100%', padding: 1 }}>
      <CardContent>
        <Stack sx={{
          gap: 2
        }}>
          <SettingsSection title="Calendar Display">
            <SettingField label="Granular time selection">
              <Box sx={{
                display: 'flex'
              }}>
                <CabSwitch
                  checked={schedulingPrefs.user_prefs.granular_time_selection}
                  onChange={(e) => schedulingPrefs.user_prefs && onSchedulingPrefChange({
                    id: schedulingPrefs.user_prefs.id,
                    granular_time_selection: e.target.checked,
                  })}
                />
              </Box>
            </SettingField>

            <Divider />
            <SettingField label="Start of the week">
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center'
                }}>
                <CabDropdown<WeekStartDay>
                  value={schedulingPrefs.user_prefs.week_start_day
                    ? WeekStartDay[schedulingPrefs.user_prefs.week_start_day]
                    : WeekStartDay.SUNDAY}
                  options={[
                    { value: WeekStartDay.SUNDAY, label: "Sunday" },
                    { value: WeekStartDay.MONDAY, label: "Monday" },
                  ]}
                  onChange={(e) => {
                    if (schedulingPrefs.user_prefs) {
                      const newWeekStartDay = e.target.value as WeekStartDay;
                      const currentWorkWeekStartDay = schedulingPrefs.user_prefs.work_week_start_day;

                      const updates: SchedulingPreferencesRequest = { week_start_day: newWeekStartDay };
                      if (newWeekStartDay === WeekStartDay.MONDAY
                      && currentWorkWeekStartDay === WeekStartDay.SUNDAY) {
                        updates.work_week_start_day = WeekStartDay.MONDAY;
                      }

                      onSchedulingPrefChange(updates);
                    }
                  }}
                />
              </Box>
            </SettingField>

            <Divider />
            <SettingField label="Start of the work week">
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center'
                }}>
                <CabDropdown<WeekStartDay>
                  value={schedulingPrefs.user_prefs.work_week_start_day
                    ? WeekStartDay[schedulingPrefs.user_prefs.work_week_start_day]
                    : WeekStartDay.MONDAY}
                  options={[
                    {
                      value: WeekStartDay.SUNDAY,
                      label: "Sunday",
                      disabled: schedulingPrefs.user_prefs.week_start_day === WeekStartDay.MONDAY
                    },
                    { value: WeekStartDay.MONDAY, label: "Monday" },
                  ]}
                  onChange={(e) => schedulingPrefs.user_prefs && onSchedulingPrefChange({
                    work_week_start_day: e.target.value as WeekStartDay
                  })}
                />
              </Box>
            </SettingField>
          </SettingsSection>

          <SectionSpacer />
          <SettingsSection title="Timezones">
            <SettingField label="Primary calendar timezone">
              <CabTimezoneInput
                alignRight
                value={schedulingPrefs.user_prefs.default_calendar_timezone || "null" }
                sx={{width: 300}}
                onChange={(value) => schedulingPrefs.user_prefs && onSchedulingPrefChange({
                  id: schedulingPrefs.user_prefs.id,
                  default_calendar_timezone: value === "null" ? null : value,
                })}
                additionalOptions={[{
                  value: 'null',
                  label: "Automatic (my location)",
                  meta: {
                    allAbbreviations: [],
                    mainCities: [],
                    group: "automatic"
                  }
                }]}
              />
            </SettingField>

            <Divider />
            <SettingField label="Display multiple timezones">
              <Box sx={{
                display: 'flex'
              }}>
                {!user?.features.MULTI_TIMEZONE && (
                  <CabIcon
                    Icon={IoLockClosedOutline}
                    alt='Locked'
                    slot='end'
                    sx={{ fontSize: 25, paddingTop: '4px' }}
                  />
                )}
                <CabSwitch
                  checked={schedulingPrefs.user_prefs.multi_timezone}
                  onChange={ user?.features.MULTI_TIMEZONE ?
                    (e) => schedulingPrefs.user_prefs && onSchedulingPrefChange({
                      id: schedulingPrefs.user_prefs.id,
                      multi_timezone: e.target.checked,
                    })
                    : openUpgradeModal
                  }
                />
              </Box>
            </SettingField>

            {secondaryTimezones?.map((tz, i) => (
              <Box key={i}>
                <Divider />
                <SettingField label="Additional timezone">
                  <Box sx={{
                    display: 'flex'
                  }}>
                    {!user?.features.MULTI_TIMEZONE && (
                      <CabIcon
                        Icon={IoLockClosedOutline}
                        alt='Locked'
                        slot='end'
                        sx={{ fontSize: 25, paddingTop: '6px', marginRight: '4px' }}
                      />
                    )}
                    <CabTimezoneInput
                      alignRight
                      value={tz || "null" }
                      sx={{width: 300}}
                      onChange={(newTz) => onUpdateDefaultSecondaryTz(i, newTz)}
                      disabled={!schedulingPrefs.user_prefs?.multi_timezone}
                      additionalOptions={[{
                        value: 'null',
                        label: "Automatic (my location)",
                        meta: {
                          allAbbreviations: [],
                          mainCities: [],
                          group: "automatic"
                        }
                      }]}
                    />

                    <CabIcon
                      Icon={IoClose}
                      onClick={() => onRemoveDefaultSecondaryTz(i)}
                      sx={{ marginLeft: 1 }}
                    />
                  </Box>
                </SettingField>
              </Box>
            ))}
            <Box sx={{
              paddingTop: 1
            }}>
              <CabTooltip title={reachedMaxTz ? MAX_TZ_MSG : ''} wrapWithSpan>
                <CabButton
                  buttonType="tertiary"
                  onClick={onAddDefaultSecondaryTz}
                  sx={{ width: 190 }}
                  icon={<CabIcon Icon={IoAdd} />}
                  disabled={!schedulingPrefs.user_prefs?.multi_timezone || reachedMaxTz}
                >
                  Add Timezone
                </CabButton>
              </CabTooltip>
            </Box>
          </SettingsSection>
        </Stack>
      </CardContent>
    </Card>
  );
};
